import VehicleConditionManager from './vehicle-condition-manager';

/**
 * Draws a picture on a canvas.
 *
 * @param {{
 *     defaultType: {},
 * }} def The default
 * @param {{}} picture The picture
 * @param {{
 *     getContext: function,
 *     height: number,
 *     width: number,
 * }} canvas The canvas
 * @param {int} availableWidth The available width to display the picture
 * @param {int} availableHeight The available height to display the picture
 * @return {void}
 */
function drawPicture(def, picture, canvas, availableWidth, availableHeight) {
    const {
        imgScaleHeight: height,
        imgScaleWidth: width,
        scale,
    } = VehicleConditionManager.getScaleAndDimensionsToDisplayPicture(picture, availableWidth, availableHeight);
    const canvasCtx = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    canvasCtx.clearRect(0, 0, availableWidth, availableHeight);
    canvasCtx.drawImage(picture.img, 0, 0, width, height);
    canvasCtx.scale(scale, scale);

    drawShapes(canvasCtx, def, picture, scale);
}

export default {
    drawPicture,
};

/**
 * Draws all the shapes of the default on a canvas.
 *
 * @param {{
 *     fillStyle: string,
 *     lineWidth: number,
 *     strokeStyle: string,
 * }} ctx The context of the canvas
 * @param {{
 *     defaultType: {},
 * }} def The default
 * @param {{}} picture The picture
 * @param {number} scale The scale to apply
 * @return {void}
 */
function drawShapes(ctx, def, picture, scale) {
    const { shapes } = picture;
    const color = def.defaultType.color;
    ctx.lineWidth = 3 / scale;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    shapes.forEach(shape => {
        switch (shape.type) {
        case 'Ellipse': {
            drawEllipse(ctx, shape);
            break;
        }
        case 'Rectangle': {
            drawRectangle(ctx, shape);
            break;
        }
        case 'LinePath': {
            drawLinePath(ctx, shape);
            break;
        }
        case 'Line': {
            drawLine(ctx, shape, scale);
            break;
        }
        case 'Polygon': {
            drawPolygon(ctx, shape);
            break;
        }
        case 'Text': {
            drawText(ctx, shape);
            break;
        }
        default: {
            break;
        }
        }
    });
}

/**
 * Draws an ellipse.
 *
 * @param {object} ctx The context of the canvas
 * @param {object} shape The shape to display
 * @return {void}
 */
function drawEllipse(ctx, shape) {
    const width = parseFloat(shape.width);
    const height = parseFloat(shape.height);
    const centerX = parseFloat(shape.x);
    const centerY = parseFloat(shape.y);
    const step = 2 * Math.PI / 50;
    const radius = width / 2;
    const scale = height / width;

    ctx.beginPath();

    for (let theta = 0; theta < 2 * Math.PI; theta += step) {
        const x = centerX + (radius * Math.cos(theta));
        const y = centerY - (scale * radius * Math.sin(theta));
        ctx.lineTo(x, y);
    }

    ctx.closePath();
    ctx.stroke();
}

/**
 * Draws a rectangle.
 *
 * @param {object} ctx The context of the canvas
 * @param {object} shape The shape to display
 * @return {void}
 */
function drawRectangle(ctx, shape) {
    ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
}

/**
 * Draws a line path.
 *
 * @param {{}} ctx The context of the canvas
 * @param {{}} shape The shape to display
 * @return {void}
 */
function drawLinePath(ctx, shape) {
    ctx.beginPath();

    if (shape.points.length > 0) {
        const { x, y } = shape.points[0];

        ctx.moveTo(x, y);
    }

    shape.points.forEach(({ x, y }) => {
        ctx.lineTo(x, y);
    });

    ctx.stroke();
}

/**
 * Draws a line of two points.
 *
 * @param {{}} ctx The context of the canvas
 * @param {{
 *     dash: boolean|undefined,
 *     from: {},
 *     to: {},
 * }} shape The shape to display
 * @param {number} scale The scale to apply
 * @return {void}
 */
function drawLine(ctx, shape, scale) {
    ctx.beginPath();

    if (shape.dash === true) {
        ctx.setLineDash([6 / scale, 6 / scale]);
    } else {
        ctx.setLineDash([]);
    }

    ctx.moveTo(shape.from.x, shape.from.y);
    ctx.lineTo(shape.to.x, shape.to.y);
    ctx.stroke();

    // Reset on solid line.
    ctx.setLineDash([]);
}

/**
 * Draws a polygon.
 *
 * @param {{}} ctx The context of the canvas
 * @param {{
 *     isClosed: boolean|undefined,
 *     points: {}[],
 * }} shape The shape to display
 * @return {void}
 */
function drawPolygon(ctx, shape) {
    ctx.beginPath();

    if (shape.points.length > 0) {
        const { x, y } = shape.points[0];

        ctx.moveTo(x, y);
    }

    shape.points.forEach(({ x, y }) => {
        ctx.lineTo(x, y);
    });

    if (shape.isClosed === true) {
        ctx.closePath();
    }

    ctx.stroke();
}

/**
 * Draws a text.
 *
 * @param {object} ctx The context of the canvas
 * @param {object} shape The shape to display
 * @return {void}
 */
function drawText(ctx, shape) {
    ctx.font = shape.font;
    ctx.fillText(shape.text, shape.x, shape.y + fixTextShapeY(shape));
}

/**
 * Corrects the font size.
 *
 * @param {object} shape The shape to display
 * @return {int} Font size
 */
function fixTextShapeY(shape) {
    return extractFontSize(shape.font);
}

/**
 * Extracts the font size of a string.
 *
 * @param {string} font string
 * @return {int} Font size
 */
function extractFontSize(font) {
    return parseInt(font.split(' ')[0], 10);
}
